import type { ReactNode, JSX} from "react";
import { cn } from "./cn";
import { IconBox } from "./IconBox";

export interface SidebarItemProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly subtitles?: readonly string[];
  readonly actionLabel?: string;
  readonly tone?: "default" | "highlight" | "muted";
}

/**
 * An item inside a SidebarSection.
 * Shows an icon, title, optional subtitles, and an optional action button.
 */
export function SidebarItem({
  icon,
  title,
  subtitles,
  actionLabel,
  tone = "default",
}: SidebarItemProps): JSX.Element {
  return (
    <div className={cn(
      "flex items-center gap-2.5 p-2.5 rounded-[10px] border border-edge bg-surface-secondary [&+&]:mt-1.5",
      tone === "muted" && "opacity-80",
      tone === "highlight" && "bg-surface-hover p-3",
    )}>
      <IconBox size="xs">{icon}</IconBox>
      <div>
        <div className="text-sm text-content">{title}</div>
        {subtitles?.map((sub) => (
          <div
            className={cn("text-xs", tone === "highlight" ? "text-accent" : "text-content-tertiary")}
            key={sub}
          >
            {sub}
          </div>
        ))}
      </div>
      {actionLabel ? (
        <button
          className="ml-auto border-none rounded-full px-2.5 py-1 bg-accent text-white text-xs font-semibold cursor-pointer"
          type="button"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}
